import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DisplayPublic } from './api';
import type { GameSnapshot } from './socket';

interface DisplayState {
  displayToken: string | null;
  display: DisplayPublic | null;
  /**
   * Latest GameSnapshot from the assigned game's Socket.IO room.
   * Includes the projected state AND the rosters + team identity so
   * the scoreboard renders real player names instead of sample data.
   * In-memory only; cleared on unpair or when no game is assigned.
   */
  liveSnapshot: GameSnapshot | null;
  /** Player id whose stats-table row should flash (PLAYER_SCORED). Cleared after the flash. */
  flashPlayerId: string | null;
  /** Player ids currently rendered as flashable rows, keyed by team side. */
  flashableRows: { home: string[]; away: string[] };
  setPaired: (token: string, display: DisplayPublic) => void;
  setDisplay: (display: DisplayPublic) => void;
  setLiveSnapshot: (snapshot: GameSnapshot | null) => void;
  setFlashPlayerId: (id: string | null) => void;
  setFlashableRows: (side: 'home' | 'away', ids: string[]) => void;
  clear: () => void;
}

export const useDisplayStore = create<DisplayState>()(
  persist(
    (set) => ({
      displayToken: null,
      display: null,
      liveSnapshot: null,
      flashPlayerId: null,
      flashableRows: { home: [], away: [] },
      setPaired: (displayToken, display) =>
        set({ displayToken, display, liveSnapshot: null }),
      setDisplay: (display) => set({ display }),
      setLiveSnapshot: (liveSnapshot) => set({ liveSnapshot }),
      setFlashPlayerId: (flashPlayerId) => set({ flashPlayerId }),
      setFlashableRows: (side, ids) =>
        set((s) => ({ flashableRows: { ...s.flashableRows, [side]: ids } })),
      clear: () =>
        set({ displayToken: null, display: null, liveSnapshot: null, flashPlayerId: null, flashableRows: { home: [], away: [] } }),
    }),
    {
      name: 'scorelync-display',
      // Persist token + display identity. Live snapshot stays in-memory.
      partialize: (s) => ({ displayToken: s.displayToken, display: s.display }),
    },
  ),
);
